import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { isClerkMode, useConfig } from "../../lib/config.js";
import {
  applyAnalyticsConsent,
  cookieConsentActive,
  OPEN_PREFERENCES_EVENT,
  readConsent,
  writeConsent,
} from "./consent.js";

type View = "hidden" | "banner" | "prefs";

/**
 * EU-only cookie-consent banner + preferences modal.
 *
 * Mounted once, app-wide, in the root layout. Self-gating: it only surfaces
 * for visitors the consent system applies to (likely EU/EEA/UK — see
 * `isLikelyEU`) who haven't yet recorded a choice. Everyone else renders
 * nothing. A footer "Cookie preferences" link re-opens it via a window event.
 *
 * Dependency-free and styled to match the dark MakerX shell. Today the
 * analytics category is dormant (no tracker loads); the wiring exists so a
 * tracker can be gated on consent later (see `applyAnalyticsConsent`).
 */
export function CookieConsentBanner() {
  // b2c (Clerk) is unbranded — present as makerdoc.ai, not MakerX.
  const productName = isClerkMode(useConfig())
    ? "makerdoc.ai"
    : "MakerX Multiplayer";
  const [view, setView] = useState<View>("hidden");
  // Pending analytics toggle while the preferences modal is open.
  const [analyticsPref, setAnalyticsPref] = useState(false);

  // Decide the initial view, and apply any previously-stored choice so the
  // dormant analytics gate reflects it on every load.
  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      applyAnalyticsConsent(stored.analytics);
      setAnalyticsPref(stored.analytics);
    }
    if (cookieConsentActive() && !stored) setView("banner");
  }, []);

  // Allow the footer (or anywhere) to re-open preferences.
  useEffect(() => {
    const open = () => {
      setAnalyticsPref(readConsent()?.analytics ?? false);
      setView("prefs");
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open);
  }, []);

  const decide = (analytics: boolean) => {
    writeConsent(analytics);
    setView("hidden");
  };

  if (view === "hidden") return null;

  if (view === "prefs") {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cc-prefs-title"
        data-testid="cookie-preferences"
      >
        <div className="w-full max-w-lg rounded-xl border mx-divider bg-[#0b0b0d] shadow-2xl max-h-[85vh] overflow-y-auto">
          <div className="px-6 py-5 border-b mx-divider">
            <h2
              id="cc-prefs-title"
              className="text-lg font-light tracking-[-0.01em]"
            >
              Cookie preferences
            </h2>
          </div>
          <div className="px-6 py-5 space-y-5 text-sm">
            <p className="text-white/55 leading-relaxed">
              We use cookies to provide the core functionality of {productName}{" "}
              and, with your consent, to understand how it is used (Google
              Analytics). You can opt in or out of each optional
              category. See our{" "}
              <Link
                to="/privacy"
                className="text-[var(--color-mx-electric)] underline underline-offset-2"
              >
                privacy policy
              </Link>{" "}
              for detail.
            </p>

            <Category
              title="Strictly necessary cookies"
              description="Required to sign you in and run the service. These can't be switched off."
              checked
              locked
            />
            <Category
              title="Analytics cookies"
              description="Let Google Analytics measure traffic and understand how the service is used, so we can improve it. No advertising. Off by default."
              checked={analyticsPref}
              onChange={setAnalyticsPref}
              testId="cc-analytics-toggle"
            />
          </div>
          <div className="px-6 py-4 border-t mx-divider flex flex-wrap gap-2 justify-end">
            <Btn onClick={() => decide(false)} variant="ghost">
              Reject all
            </Btn>
            <Btn onClick={() => decide(true)} variant="ghost">
              Accept all
            </Btn>
            <Btn onClick={() => decide(analyticsPref)} variant="primary">
              Save preferences
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  // Banner
  return (
    <div
      className="fixed bottom-4 right-4 z-[100] w-[calc(100%-2rem)] sm:w-[28rem] rounded-xl border mx-divider bg-[#0b0b0d] shadow-2xl"
      role="dialog"
      aria-labelledby="cc-banner-title"
      aria-describedby="cc-banner-desc"
      data-testid="cookie-banner"
    >
      <div className="px-5 py-4">
        <h2
          id="cc-banner-title"
          className="text-base font-medium tracking-[-0.01em] mb-1.5"
        >
          Your privacy
        </h2>
        <p
          id="cc-banner-desc"
          className="text-white/55 text-[13px] leading-relaxed"
        >
          {productName} uses cookies to sign you in and run the service, and —
          with your consent — Google Analytics cookies to understand how
          it&apos;s used. If you reject optional cookies, only the strictly
          necessary ones are used.
        </p>
        <div className="mt-3 flex items-center gap-3 text-xs text-white/40">
          <Link
            to="/privacy"
            className="hover:text-white/70 underline underline-offset-2"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="hover:text-white/70 underline underline-offset-2"
          >
            Terms of Service
          </Link>
        </div>
      </div>
      <div className="px-5 py-3 border-t mx-divider flex flex-wrap gap-2 justify-end">
        <Btn onClick={() => setView("prefs")} variant="ghost">
          Manage preferences
        </Btn>
        <Btn onClick={() => decide(false)} variant="ghost">
          Reject all
        </Btn>
        <Btn onClick={() => decide(true)} variant="primary">
          Accept all
        </Btn>
      </div>
    </div>
  );
}

function Btn({
  children,
  onClick,
  variant,
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant: "primary" | "ghost";
}) {
  const base =
    "px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap";
  const styles =
    variant === "primary"
      ? "bg-[var(--color-mx-electric)] text-black hover:opacity-90"
      : "border mx-divider text-white/70 hover:text-white hover:bg-white/[0.06]";
  return (
    <button type="button" onClick={onClick} className={`${base} ${styles}`}>
      {children}
    </button>
  );
}

function Category({
  title,
  description,
  checked,
  locked,
  onChange,
  testId,
}: {
  title: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (next: boolean) => void;
  testId?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border mx-divider px-4 py-3">
      <div>
        <p className="text-white/85 font-medium text-[13px]">{title}</p>
        <p className="text-white/45 text-xs leading-relaxed mt-0.5">
          {description}
        </p>
      </div>
      <label className="shrink-0 mt-0.5 inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          disabled={locked}
          onChange={(e) => onChange?.(e.target.checked)}
          data-testid={testId}
        />
        <span
          className={`w-9 h-5 rounded-full relative transition-colors ${
            checked ? "bg-[var(--color-mx-electric)]" : "bg-white/15"
          } ${locked ? "opacity-60" : ""} after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:rounded-full after:bg-white after:transition-transform ${
            checked ? "after:translate-x-4" : ""
          }`}
        />
      </label>
    </div>
  );
}
